import { BodyText, FlexColumn, Input } from '@make-software/cspr-design';
import React, { useEffect, useState } from 'react';
import { PluginFormControlsProps } from 'app/components/management/common/plugins/form-control-map';
import { InfoAccordion } from 'app/components/management/common/info-accordion/info-accordion';
import { AccordionRow } from 'app/components/management/common/plugins/action-based/form-controls';
import { DropdownStyles, ErrorText, ErrorTextContainer } from 'app/components/common/common-styles';
import { IntegrationInstructions } from 'app/components/management/common/plugins/custom/integration-instructions';
import Select from 'react-select';

type ActionOption = {
  label: string;
  value: string;
};

export const CustomPluginFormControls = ({
  metadata,
  errors,
  onChange
}: PluginFormControlsProps) => {
  const noneAction = { label: 'none', value: 'none' };
  const actionOptions = [
    noneAction,
    { label: 'Redirect', value: 'redirect' },
    { label: 'Init Widget', value: 'initWidget' }
  ];
  const [action, setAction] = useState(noneAction.value);
  const [targetURL, setTargetURL] = useState('');
  const [verifyURL, setVerifyURL] = useState('');
  const [widgetUrl, setWidgetUrl] = useState('');

  useEffect(() => {
    setTargetURL(metadata?.targetURL || '');
    setVerifyURL(metadata?.verifyURL || '');
    setWidgetUrl(metadata?.widgetUrl || '');
    setAction(metadata?.action || noneAction.value);
  }, [metadata]);

  const emit = (patch: Partial<{ action: string; targetURL: string; verifyURL: string; widgetUrl: string }>) => {
    onChange({ action, targetURL, verifyURL, widgetUrl, ...patch });
  };

  const handleActionChange = (selectedOption: ActionOption | null) => {
    const value = selectedOption ? selectedOption.value : noneAction.value;
    setAction(value);
    emit({ action: value });
  };

  const handleTargetURLChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTargetURL(value);
    emit({ targetURL: value });
  };

  const handleVerifyURLChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setVerifyURL(value);
    emit({ verifyURL: value });
  };

  const handleWidgetUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setWidgetUrl(value);
    emit({ widgetUrl: value });
  };

  const selected = actionOptions.filter((o) => action === o.value);

  return (
    <FlexColumn gap={8}>
      <FlexColumn>
        <BodyText size={1} scale={'sm'}>
          Action
        </BodyText>
        <Select<ActionOption>
          options={actionOptions}
          value={selected}
          isSearchable={false}
          styles={DropdownStyles()}
          onChange={(selectedOption) => handleActionChange(selectedOption)}
        ></Select>
      </FlexColumn>
      {action === 'redirect' && (
        <Input
          value={targetURL}
          label={<BodyText size={1}>Target URL</BodyText>}
          placeholder="https://redirect.com"
          onChange={handleTargetURLChange}
        />
      )}
      {action === 'initWidget' && (
        <>
          <Input
            value={widgetUrl}
            label={<BodyText size={1}>Widget URL</BodyText>}
            placeholder="https://example.com/static/widget.js"
            onChange={handleWidgetUrlChange}
          />
          {errors?.pluginMetadata?.widgetUrl && (
            <ErrorTextContainer justify={'flex-start'}>
              <ErrorText size={3}>{errors?.pluginMetadata?.widgetUrl?.message}</ErrorText>
            </ErrorTextContainer>
          )}
        </>
      )}
      <Input
        value={verifyURL}
        label={<BodyText size={1}>Verification URL</BodyText>}
        placeholder="https://verify.com"
        onChange={handleVerifyURLChange}
      />
      {errors?.pluginMetadata?.verifyURL && (
        <ErrorTextContainer justify={'flex-start'}>
          <ErrorText size={3}>{errors?.pluginMetadata?.verifyURL?.message}</ErrorText>
        </ErrorTextContainer>
      )}
      <AccordionRow>
        <InfoAccordion
          disableClickAway={true}
          renderContent={() => <IntegrationInstructions url={verifyURL} />}
        >
          Integration instructions
        </InfoAccordion>
      </AccordionRow>
    </FlexColumn>
  );
};
